import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue'

import { MATCH_MODES, type FilterMatchMode, type ReportFilter } from '../models/filter.types'
import {
  IMPLEMENTED_REPORT_TABLE_TYPES,
  type ReportTableSavedViewDraft,
  type ReportTableSavedViewV1,
  type ReportTableType,
} from '../models/tablePresentation.types'
import { useCustomizeStore } from '../stores/customizeStore'

import { useAccount, useWorkspace } from '@/features/account'

const CACHE_KIND = 'namedTableViewsV1'
const MATCH_MODE_SET = new Set<FilterMatchMode>(MATCH_MODES)
type NormalizedReportTableSavedViewV1 = ReportTableSavedViewV1 & { expandedFields: string[] }

interface SavedViewCacheV1 {
  version: 1
  selectedId: string | null
  views: NormalizedReportTableSavedViewV1[]
}

export interface UseLocalReportTableViewsOptions {
  id: MaybeRefOrGetter<string>
  partitionName?: MaybeRefOrGetter<string | undefined>
  seedViews?: MaybeRefOrGetter<readonly ReportTableSavedViewV1[] | undefined>
  idFactory?: () => string
  now?: () => Date
}

export interface UseLocalReportTableViews {
  views: Ref<NormalizedReportTableSavedViewV1[]>
  selectedId: Ref<string | null>
  selected: Readonly<Ref<NormalizedReportTableSavedViewV1 | null>>
  save: (draft: ReportTableSavedViewDraft, id?: string) => NormalizedReportTableSavedViewV1
  select: (id: string | null) => NormalizedReportTableSavedViewV1 | null
  remove: (id: string) => void
}

export type LocalReportTableViewErrorCode = 'blank_name' | 'duplicate_name' | 'not_found'

export class LocalReportTableViewError extends Error {
  constructor(readonly code: LocalReportTableViewErrorCode) {
    super(code)
    this.name = 'LocalReportTableViewError'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function uniqueStrings(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) return null
  return [...new Set(value as string[])]
}

function parseFilter(value: unknown): ReportFilter | null {
  if (!isRecord(value) || typeof value.field !== 'string') return null
  if (
    typeof value.match_mode !== 'string' ||
    !MATCH_MODE_SET.has(value.match_mode as FilterMatchMode)
  )
    return null
  return {
    field: value.field,
    match_mode: value.match_mode as FilterMatchMode,
    value: value.value,
    ...(typeof value.is_change === 'boolean' ? { is_change: value.is_change } : {}),
    ...(value.direction === 'increase' || value.direction === 'decrease'
      ? { direction: value.direction }
      : {}),
    ...(typeof value.sub_type === 'string'
      ? { sub_type: value.sub_type as NonNullable<ReportFilter['sub_type']> }
      : {}),
  }
}

function parseView(value: unknown): NormalizedReportTableSavedViewV1 | null {
  if (!isRecord(value) || value.version !== 1) return null
  if (
    typeof value.id !== 'string' ||
    !value.id ||
    typeof value.name !== 'string' ||
    !value.name.trim() ||
    typeof value.updatedAt !== 'string'
  )
    return null
  const visibleFields = uniqueStrings(value.visibleFields)
  const columnOrder = uniqueStrings(value.columnOrder)
  const frozenFields = uniqueStrings(value.frozenFields)
  const expandedFields =
    value.expandedFields === undefined ? [] : uniqueStrings(value.expandedFields)
  const tableType = value.tableType as ReportTableType
  const filters = Array.isArray(value.filters) ? value.filters.map(parseFilter) : []
  if (
    !visibleFields ||
    !columnOrder ||
    !frozenFields ||
    !expandedFields ||
    !IMPLEMENTED_REPORT_TABLE_TYPES.includes(
      tableType as (typeof IMPLEMENTED_REPORT_TABLE_TYPES)[number],
    ) ||
    !Array.isArray(value.filters) ||
    filters.some((filter) => filter === null) ||
    (value.breakdownField !== null && typeof value.breakdownField !== 'string')
  )
    return null
  const visible = new Set(visibleFields)
  return {
    version: 1,
    id: value.id,
    name: value.name.trim(),
    updatedAt: value.updatedAt,
    visibleFields,
    columnOrder,
    frozenFields: frozenFields.filter((field) => visible.has(field)),
    expandedFields: expandedFields.filter((field) => visible.has(field)),
    tableType,
    breakdownField: value.breakdownField,
    filters: filters as ReportFilter[],
  }
}

function parseCache(value: unknown): SavedViewCacheV1 | null {
  if (!isRecord(value) || value.version !== 1 || !Array.isArray(value.views)) return null
  const views = value.views.map(parseView)
  if (views.some((view) => view === null)) return null
  const parsed = views as NormalizedReportTableSavedViewV1[]
  const selectedId =
    typeof value.selectedId === 'string' && parsed.some((view) => view.id === value.selectedId)
      ? value.selectedId
      : null
  return { version: 1, selectedId, views: parsed }
}

function cloneView(view: NormalizedReportTableSavedViewV1): NormalizedReportTableSavedViewV1 {
  return {
    ...view,
    visibleFields: [...view.visibleFields],
    columnOrder: [...view.columnOrder],
    frozenFields: [...view.frozenFields],
    expandedFields: [...view.expandedFields],
    filters: view.filters.map((filter) => ({
      ...filter,
      value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
    })),
  }
}

function createId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `view-${Date.now()}`
}

/** Scoped local named views; injected seeds keep the storage owner replaceable without UI forks. */
export function useLocalReportTableViews(
  options: UseLocalReportTableViewsOptions,
): UseLocalReportTableViews {
  const account = useAccount()
  const workspace = useWorkspace()
  const cache = useCustomizeStore()
  const views = ref<NormalizedReportTableSavedViewV1[]>([])
  const selectedId = ref<string | null>(null)
  const selected = computed(() => views.value.find((view) => view.id === selectedId.value) ?? null)
  const partition = computed(() => {
    const userId = account.userId.value
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (userId === undefined || accountId === undefined || workspaceId === undefined) return null
    return `report-table-views:${userId}:${accountId}:${String(workspaceId)}:${
      toValue(options.partitionName) || 'default'
    }:${toValue(options.id)}`
  })

  function persist(): void {
    if (!partition.value) return
    cache.setCached<SavedViewCacheV1>(partition.value, CACHE_KIND, {
      version: 1,
      selectedId: selectedId.value,
      views: views.value.map(cloneView),
    })
  }

  function hydrate(): void {
    const stored = partition.value
      ? parseCache(cache.getCached<unknown>(partition.value, CACHE_KIND))
      : null
    const seeded = (toValue(options.seedViews) ?? []).flatMap((view) => parseView(view) ?? [])
    views.value = (stored?.views ?? seeded).map(cloneView)
    selectedId.value = stored?.selectedId ?? null
  }

  watch(partition, hydrate, { immediate: true })
  watch(() => toValue(options.seedViews), hydrate, { deep: true })

  function save(draft: ReportTableSavedViewDraft, id?: string): NormalizedReportTableSavedViewV1 {
    const name = draft.name.trim()
    if (!name) throw new LocalReportTableViewError('blank_name')
    const requested = id ? views.value.find((view) => view.id === id) : undefined
    if (id && !requested) throw new LocalReportTableViewError('not_found')
    const duplicate = views.value.find(
      (view) => view.id !== id && view.name.trim().toLowerCase() === name.toLowerCase(),
    )
    if (id && duplicate) throw new LocalReportTableViewError('duplicate_name')
    const existingId = requested?.id ?? duplicate?.id
    const visible = new Set(draft.visibleFields)
    const view: NormalizedReportTableSavedViewV1 = {
      ...draft,
      version: 1,
      id: existingId ?? options.idFactory?.() ?? createId(),
      updatedAt: (options.now?.() ?? new Date()).toISOString(),
      name,
      visibleFields: [...visible],
      columnOrder: [...new Set(draft.columnOrder)],
      frozenFields: [...new Set(draft.frozenFields)].filter((field) => visible.has(field)),
      expandedFields: [...new Set(draft.expandedFields ?? [])].filter((field) =>
        visible.has(field),
      ),
      filters: draft.filters.map((filter) => ({
        ...filter,
        value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
      })),
    }
    views.value = existingId
      ? views.value.map((current) => (current.id === existingId ? view : current))
      : [...views.value, view]
    selectedId.value = view.id
    persist()
    return cloneView(view)
  }

  function select(id: string | null): NormalizedReportTableSavedViewV1 | null {
    selectedId.value = id && views.value.some((view) => view.id === id) ? id : null
    persist()
    return selected.value ? cloneView(selected.value) : null
  }

  function remove(id: string): void {
    views.value = views.value.filter((view) => view.id !== id)
    if (selectedId.value === id) selectedId.value = null
    persist()
  }

  return { views, selectedId, selected, save, select, remove }
}
